import { Fragment, ReactNode } from "react";
import { useIonToast } from "@ionic/react";
import { DateTime } from "luxon";
import { ShortenedDays } from "model";
import { TimeInput } from "swing-components";

import { addOneMinute, isoToMilitaryTime, militaryTimeToISO, subtractOneMinute } from "./utils";

type OnUpdateProps = (value: number, day: ShortenedDays, slot: "start" | "end") => void;

type DayHours = {
  start: number;
  end: number;
};

type WeekHours = {
  mon: DayHours;
  tue: DayHours;
  wed: DayHours;
  thu: DayHours;
  fri: DayHours;
};

type EditableHoursProps = {
  editing: boolean;
  value: WeekHours;
  onChange: OnUpdateProps;
};

type HoursTableSkeletonProps = {
  children: ReactNode;
};

function HoursTableSkeleton(props: HoursTableSkeletonProps) {
  const { children } = props;

  return (
    <div
      css={{
        display: "grid",
        gridRowGap: 24,
        gridColumnGap: 6,
        gridTemplateColumns: "repeat(3, 1fr)",
        width: "100%",
      }}
    >
      <div className="font-medium">Day</div>
      <div className="font-medium">Start</div>
      <div className="font-medium">End</div>
      {children}
    </div>
  );
}

export const EditableHours = ({ editing, value, onChange }: EditableHoursProps) => {
  const [present] = useIonToast();
  const onUpdate = (time: DateTime, day: ShortenedDays, slot: "start" | "end") => {
    let updatedTime = isoToMilitaryTime(time.toString());

    // Automatically fix start and end times being greater or less than their counterparts.
    switch (slot) {
      case "start":
        if (updatedTime >= value[day]["end"]) {
          present({
            position: "top",
            message: "Start time cannot come after end time.",
            duration: 2000,
          });
          updatedTime = subtractOneMinute(value[day]["end"]);
        }
        break;
      case "end":
        if (updatedTime <= value[day]["start"]) {
          present({
            position: "top",
            message: "End time cannot come before start time.",
            duration: 2000,
          });
          updatedTime = addOneMinute(value[day]["start"]);
        }
        break;
      default:
        break;
    }
    onChange(updatedTime, day, slot);
  };

  type EditableHoursItem = {
    displayName: string;
    dayCode: ShortenedDays;
  };

  const EditableHoursStructure: EditableHoursItem[] = [
    {
      displayName: "Monday",
      dayCode: "mon",
    },
    {
      displayName: "Tuesday",
      dayCode: "tue",
    },
    {
      displayName: "Wednesday",
      dayCode: "wed",
    },
    {
      displayName: "Thursday",
      dayCode: "thu",
    },
    {
      displayName: "Friday",
      dayCode: "fri",
    },
  ];

  const renderDaySchedule = (displayName: string, dayCode: ShortenedDays) => (
    <>
      <div className="items-center flex">{displayName}</div>
      <div className="flex">
        <TimeInput
          readOnly={!editing}
          value={DateTime.fromISO(militaryTimeToISO(value[dayCode].start)).toUTC()}
          onChange={(time) => onUpdate(time, dayCode, "start")}
        />
      </div>
      <div className="flex">
        <TimeInput
          readOnly={!editing}
          value={DateTime.fromISO(militaryTimeToISO(value[dayCode].end)).toUTC()}
          onChange={(time) => onUpdate(time, dayCode, "end")}
        />
      </div>
    </>
  );

  return (
    <HoursTableSkeleton>
      {EditableHoursStructure.map((item) => (
        <Fragment key={item.dayCode}>{renderDaySchedule(item.displayName, item.dayCode)}</Fragment>
      ))}
    </HoursTableSkeleton>
  );
};
